import type { Report, ServiceItem, ServiceTransaction } from '../../types';
import type { JournalMirrorRequest, JournalSource } from '../../services/serviceJournal';
import { isHourlyUnit } from '../../services/serviceUnits';
import { hatBestandteile } from '../../services/bestandteile/bestandteilLogic';

/**
 * Die Positionsbuchung eines Dokumentmoduls nach Freigabe bzw. Versand —
 * Fall 1 des Abschlussschritts (Plan Paketposition B5) fuer die Module, die
 * bisher nicht buchten: A+S-Plan und Handbuch.
 *
 * Dieselbe Rechnung wie in Vorankuendigung, Unterlage und SiGe-Plan (dort
 * inline gewachsen): eine Stundenposition bucht die eingetragenen Stunden,
 * jede andere genau 1; `done`, sobald das Soll erreicht ist. Dieselbe Form
 * des Protokolleintrags (kein `bookedAt`: das Feld traegt im Altbestand die
 * Bedeutung „abgerechnet", siehe serviceLogUtils.stampLegacyEntry).
 *
 * REIN: liest und schreibt nichts. Zurueck kommen der neue Stand (fuer
 * `onUpdate`) und die Vormerkung der Zweitschrift ins Buchungsjournal (fuer
 * `queueJournalMirror`, Muster B: geschrieben wird erst nach dem bestaetigten
 * Flush). Ohne belastbare Projekt-id (`temp-id`) wird NICHTS vorgemerkt.
 *
 * `null`, wenn die Position im Stand nicht steht — der Aufrufer schreibt dann
 * nichts. Ebenfalls `null` fuer eine PAKETPOSITION (zweite Sperre hinter der
 * Auswahl, Plan Kernregel 7): ein Paket bucht nur der Abschlussschritt, wenn
 * sein letzter Bestandteil erledigt ist.
 */
export interface ModulBuchung {
    report: Report;
    logEntry: ServiceTransaction;
    vormerkung: JournalMirrorRequest | null;
}

export const planeModulBuchung = (a: {
    report: Report;
    serviceId: string;
    /** Eingabe des Dialogs; zaehlt nur bei Stundenpositionen. */
    hours: number;
    /** Protokolltext aus Menge und Einheit. */
    beschreibung: (menge: number, einheit: string) => string;
    /** Wer die Leistung erbracht hat; leer → 'System'. */
    performerName: string;
    source: JournalSource;
    jetzt?: Date;
    neueId?: () => string;
}): ModulBuchung | null => {
    const globalData = a.report?.metadata?.globalData;
    if (!globalData) return null;
    const services: ServiceItem[] = globalData.services || [];
    const index = services.findIndex(s => s && s.id === a.serviceId);
    if (index === -1) return null;

    const s = services[index];
    if (hatBestandteile(s)) return null;
    const stunden = Number(a.hours);
    const menge = isHourlyUnit(s.unit) ? (Number.isFinite(stunden) && stunden > 0 ? stunden : 1) : 1;
    const neuGebucht = (s.quantityUsed || 0) + menge;
    const fertig = s.quantityContracted > 0 && neuGebucht >= s.quantityContracted;
    const neu = [...services];
    neu[index] = { ...s, quantityUsed: neuGebucht, status: fertig ? 'done' : s.status };

    const jetzt = a.jetzt instanceof Date && Number.isFinite(a.jetzt.getTime()) ? a.jetzt : new Date();
    const logEntry: ServiceTransaction = {
        id: (a.neueId ?? (() => crypto.randomUUID()))(),
        date: jetzt.toISOString(),
        serviceId: s.id,
        quantity: menge,
        performerId: 'system',
        performerName: (a.performerName || '').trim() || 'System',
        isSubstitution: false,
        description: a.beschreibung(menge, s.unit || 'Stück'),
    };

    const previousLog = globalData.serviceLog || [];
    const projectId = (globalData.projectId || '').trim();
    const vormerkung: JournalMirrorRequest | null = projectId && projectId !== 'temp-id'
        ? {
            projectId,
            entries: [logEntry],
            previousLog,
            source: a.source,
            linkedProjectConfig: globalData.linkedProjectConfig ?? null,
            // Sicher gewusst: die Ausgabe einer Unterlage ist weder
            // Zusatzleistung noch Nachweis einer BEGEHUNG.
            statements: { [logEntry.id]: { isAddon: false, coversInspection: false } },
        }
        : null;

    return {
        report: {
            ...a.report,
            metadata: {
                ...a.report.metadata,
                globalData: { ...globalData, services: neu, serviceLog: [...previousLog, logEntry] },
            },
        },
        logEntry,
        vormerkung,
    };
};
